import React from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { withTranslation } from "react-i18next";

import axios from "../../components/Axios";
import { CompactSelect, copyText } from "./ui";

const MCP_URL = process.env.REACT_APP_MCP_URL;
const SERVER_NAME = "videoviews";

// Account > AI Assistants: MCP connection details and API keys
class Index extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      items: [],
      // the key the header value is built from
      keyId: "",
    };
  }

  componentDidMount() {
    this.getItems();
  }

  setItems(items) {
    const keyId = items.some((x) => x._id === this.state.keyId) ? this.state.keyId : items.length ? items[0]._id : "";
    this.setState({ loading: false, items, keyId });
  }

  async getItems() {
    this.setState({ loading: true });
    let resp = await axios.get("/user/mcp_tokens");
    this.setItems(resp.data);
  }

  createItem = async () => {
    this.setState({ loading: true });
    await axios.post("/user/mcp_tokens", { name: "" });
    this.getItems();
  };

  async deleteItem(id) {
    this.setState({ loading: true });
    let resp = await axios.delete("/user/mcp_tokens", { data: { _id: id } });
    this.setItems(resp.data);
  }

  formatDate(u) {
    let d = new Date(u.slice(0, -3) + "Z");
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }

  mask(id) {
    return id.slice(0, 10) + "…" + id.slice(-10);
  }

  // one connection detail: label (with the name other apps use for the field), value (text or a node),
  // a hint under the value, and a copy button when there is something to copy
  row(label, alt, value, hint, copyValue) {
    const { t } = this.props;
    return (
      <div>
        <dt>
          <span>{label}</span>
          {alt && <span className="ac-alt">{alt}</span>}
        </dt>
        <dd>
          <span className="ac-val">{value}</span>
          {hint && <span className="ac-hint">{hint}</span>}
        </dd>
        {copyValue && (
          <button type="button" className="ac-sq" title={t("Copy")} aria-label={t("Copy") + " " + label} onClick={() => copyText(copyValue, t("Copied to clipboard"))}>
            <ContentCopyIcon />
          </button>
        )}
      </div>
    );
  }

  render() {
    const { t } = this.props;
    const { loading, items, keyId } = this.state;
    const copied = t("Copied to clipboard");

    return (
      <div className="ac-grid">
        <section className="ac-card ac-w3" aria-labelledby="ac-connection-h">
          <div className="ac-head">
            <h2 id="ac-connection-h" className="ac-title">
              {t("Connection Details")}
            </h2>
          </div>
          <div className="ac-body" style={{ gap: "10px", paddingBottom: "16px" }}>
            <p className="ac-desc">
              {t("Add VideoViews as a custom connector (or custom MCP server) in your AI app, using the values below. Field names vary between apps")}
            </p>
            <dl className="ac-kv">
              {this.row(t("Name"), null, SERVER_NAME, t("Any name you like"), SERVER_NAME)}
              {this.row(t("URL"), t("or Server URL"), MCP_URL, null, MCP_URL)}
              {this.row(t("Type"), t("or Transport"), "Streamable HTTP", t("Only if asked, sometimes just HTTP"))}
            </dl>

            <div className="ac-options">
              <span className="ac-overline">{t("Authentication")}</span>
              <p className="ac-desc">
                {t("If your app asks how to sign in, choose No sign-in. Then add this header, and leave fields like “Bearer token env var” empty")}
              </p>
              <dl className="ac-kv">
                {this.row(t("Header name"), t("or Key"), "Authorization", null, "Authorization")}
                {keyId
                  ? this.row(
                      t("Header value"),
                      t("or Value"),
                      <span className="ac-pair">
                        <span>Bearer</span>
                        {items.length > 1 ? (
                          <CompactSelect
                            label={t("API key")}
                            value={keyId}
                            onChange={(e) => this.setState({ keyId: e.target.value })}
                            options={items.map((x) => [x._id, this.mask(x._id)])}
                          />
                        ) : (
                          <span>{this.mask(keyId)}</span>
                        )}
                      </span>,
                      t("Bearer, a space, then your API key. Copy fills in the whole key"),
                      "Bearer " + keyId
                    )
                  : this.row(t("Header value"), t("or Value"), <span className="ac-note">{t("Create an API key first")}</span>)}
              </dl>
            </div>
          </div>
        </section>

        <section className="ac-card ac-w2" aria-labelledby="ac-keys-h">
          <div className="ac-head">
            <h2 id="ac-keys-h" className="ac-title">
              {t("API Keys")}
            </h2>
            {!loading && <span className="ac-badge">{items.length}</span>}
            <span className="ac-spacer" />
            <Button className="Button ButtonPrimary ac-btn" startIcon={<AddIcon />} onClick={this.createItem} disabled={loading}>
              {t("Create API Key")}
            </Button>
          </div>
          {loading && items.length === 0 && <CircularProgress size={24} />}
          {!loading && items.length === 0 && <div className="ac-empty">{t("No API keys yet")}</div>}
          {items.map((item) => (
            <div key={item._id} className="ac-item ac-key">
              <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "1px" }}>
                <span className="ac-keytext">{this.mask(item._id)}</span>
                <span className="ac-meta">{t("Created {{date}}", { date: this.formatDate(item.created_at) })}</span>
              </div>
              <button type="button" className="ac-sq" title={t("Copy")} aria-label={t("Copy")} onClick={() => copyText(item._id, copied)}>
                <ContentCopyIcon />
              </button>
              <button type="button" className="ac-sq" title={t("Delete Forever")} aria-label={t("Delete Forever")} onClick={() => this.deleteItem(item._id)} disabled={loading}>
                <DeleteIcon />
              </button>
            </div>
          ))}
        </section>
      </div>
    );
  }
}

export default withTranslation()(Index);
